/**
 * Feb-2026 (User-Wunsch 24 May Iter 27) — Preise B2C / B2B / Vermittlungs-
 * provision wurden aus „Fahrzeug-Stammdaten" (BasicsSection) extrahiert und
 * unterhalb der „Bilder & Video"-Sektion platziert. Eigenständige Komponente
 * mit lokalem KI-Preisvorschlag-State, damit BasicsSection schlanker bleibt.
 */
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2, Info, ChevronRight, BarChart3, Hourglass } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import SectionToggle from "./SectionToggle";
import { CircleDollarSign } from "lucide-react";
import QualityScoreCard from "./QualityScoreCard";
import PlausibilityCheckBanner from "./PlausibilityCheckBanner";
import { useBasicsCollapse } from "./BasicsCollapseContext";
// EinstandCalcBlock wurde Jul-2026 in den Bereich „Zustand & Schadensermittlung" verschoben (CreateListingPage).
import DamageAssessmentBlock from "./DamageAssessmentBlock";  // noqa: F401 (Block auf User-Wunsch ausgeblendet)

export default function PriceFieldsBlock({ form, update, isDealer, isAuction, listingId, orderMode = false }) {
    const { t } = useTranslation();
    const { closeBasicsIfOpen } = useBasicsCollapse();
    // Jul-2026 (User-Wunsch): Bereich startet immer eingeklappt.
    const [pricesOpen, setPricesOpen] = useState(false);
    const [priceAi, setPriceAi] = useState({ loading: false, data: null });
    // Feb-2026 Iter 28 — Wird inkrementiert, wenn der User die Sektion öffnet
    // ODER auf den „KI neu berechnen"-Button im Header klickt. Plausibilitäts-
    // Check / Quality-Score / Preisvorschlag re-triggern auf Änderung.
    const [aiRefreshSeq, setAiRefreshSeq] = useState(0);
    // Jul-2026 (User-Wunsch) — Kalkulations-Kontext in der KI-Karte:
    // Einkaufspreis + Werkstattkosten (interne Aufträge) + Schadensabzug.
    // Schalter „Schaden berücksichtigen" ist standardmäßig AUS.
    const [includeDamage, setIncludeDamage] = useState(false);
    const [costSummary, setCostSummary] = useState(null);
    useEffect(() => {
        if (!listingId) return;
        let active = true;
        api.get(`/listings/${listingId}/dispo/cost-summary`)
            .then((r) => { if (active) setCostSummary(r.data); })
            .catch(() => { if (active) setCostSummary(null); });
        return () => { active = false; };
    }, [listingId]);
    const ekVal = Number(form.purchase_price) || 0;
    const workshopTotal = Number(costSummary?.workshop_total_gross) || 0;
    const damageDed = Number(form.condition_assessment?.deduction_total) || 0;
    const reqCls = () => "border-2 border-red-400";

    // Jun-2026 (User-Wunsch) — Bei ausweisbarer MwSt. (vat_included !== false)
    // wird unter jedem Brutto-Preis dezent (klein, grau) der Netto-Betrag
    // (Brutto / 1,19) angezeigt. Bei Differenzbesteuerung (§25a) ausgeblendet.
    const VAT_RATE = 0.19;
    const showNet = form.vat_included !== false;
    const netHint = (value, testId) => {
        const g = Number(value) || 0;
        if (!showNet || g <= 0) return null;
        const net = g / (1 + VAT_RATE);
        return (
            <div className="mt-0.5 text-[10px] text-gray-400 font-mono-num" data-testid={testId}>
                netto {net.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
        );
    };

    // Feb-2026 (User-Wunsch 27.05.2026) — Margen-Rechner. Sobald der Händler
    // sowohl `price` (B2C) als auch `price_b2b` ausfüllt, schlägt das System
    // automatisch die Vermittlungsprovision = `price − price_b2b` vor und
    // füllt das Feld vor, solange der Händler es nicht manuell überschrieben
    // hat. Spart 1 Eingabeschritt + verhindert Tippfehler.
    const lastAutoCalcRef = useRef(null);
    useEffect(() => {
        const p = Number(form.price) || 0;
        const b = Number(form.price_b2b) || 0;
        if (p <= 0 || b <= 0) return;
        const diff = Math.max(0, Math.round(p - b));
        const currentRaw = form.referral_commission_eur;
        const current = currentRaw === "" || currentRaw == null ? null : Number(currentRaw);
        // Nur vorbefüllen, wenn:
        //  * das Feld leer ist, ODER
        //  * der bisherige Wert exakt der letzten Auto-Kalkulation entspricht
        //    (= User hat nicht manuell überschrieben)
        if (current === null || current === lastAutoCalcRef.current) {
            if (current !== diff) {
                update("referral_commission_eur", String(diff));
                lastAutoCalcRef.current = diff;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.price, form.price_b2b]);

    const buildTitleSuggestion = () => {
        const ezY = form.first_registration_date
            ? new Date(form.first_registration_date).getFullYear()
            : form.year;
        const km = Number(form.mileage) || 0;
        const parts = [
            [form.brand, form.model].filter(Boolean).join(" "),
            ezY ? `EZ ${ezY}` : null,
            km > 0 ? `${km.toLocaleString("de-DE")} km` : null,
            form.condition === "Neufahrzeug" ? "Neu" : null,
        ].filter(Boolean);
        return parts.length ? parts.join(", ") : "";
    };

    const runAllAi = (silent = false) => {
        setAiRefreshSeq((n) => n + 1);
        if (form.brand && form.model) {
            // KI-Preisvorschlag automatisch mitfeuern + leere Preisfelder befüllen.
            fetchPriceSuggestion(silent, true);
            // Feb-2026 (User-Wunsch 26 May): Titel-Vorschlag bei „Alle KI
            // aktualisieren" ebenfalls regenerieren. Nur überschreiben, wenn
            // (a) noch leer ODER (b) explizit nicht-silent (Manueller Klick).
            const suggestion = buildTitleSuggestion();
            if (suggestion) {
                const currentTitle = (form.title || "").trim();
                if (!currentTitle || !silent) {
                    update("title", suggestion);
                    if (!silent && currentTitle && currentTitle !== suggestion) {
                        toast.success("Titel-Vorschlag aktualisiert — bei Bedarf manuell anpassen.", { duration: 4000 });
                    }
                }
            }
        }
    };

    const fetchPriceSuggestion = async (silent = false, autoApply = false) => {
        if (!form.brand || !form.model) {
            if (!silent) toast.info("Bitte zuerst Marke + Modell ausfüllen.");
            return;
        }
        if (priceAi.loading) return;  // Race-Schutz: keinen zweiten Parallel-Request starten
        // Jul-2026 (User-Wunsch) — vor der KI-Preisanfrage prüfen, ob die KI-/KBA-Daten
        // (HSN/TSN + Werksdaten) bereits gelaufen sind. Wenn nicht: Hinweis zur besseren
        // Genauigkeit + Korrektur-Lesen der übernommenen Daten. Blockiert NICHT.
        if (!silent) {
            const kbaReady = !!(form.hsn && form.tsn && (form.displacement || form.power_kw));
            if (!kbaReady) {
                toast.warning(
                    "Für einen genaueren Preis zuerst die KI-/KBA-Daten (HSN + TSN → Button ‚KBA Daten') laufen lassen. Hinweis: automatisch übernommene Daten können fehlerhaft sein — bitte anschließend Korrektur lesen.",
                    { duration: 8000 }
                );
            }
        }
        setPriceAi({ loading: true, data: null });
        try {
            const { data } = await api.post("/listings/ai-price-suggest", {
                brand: form.brand,
                model: form.model,
                hsn: form.hsn || null,
                tsn: form.tsn || null,
                year: form.first_registration_date
                    ? parseInt(form.first_registration_date.slice(0, 4), 10)
                    : form.year,
                mileage: Number(form.mileage) || 0,
                condition: form.condition,
                category: form.category,
                fuel: form.fuel,
                power_kw: form.power_kw ? Number(form.power_kw) : null,
                power_hp: form.power_hp ? Number(form.power_hp) : null,
                features: form.features || [],
                condition_assessment: form.condition_assessment || null,
                damage_deduction: damageDed,
                consider_damage: includeDamage,
            });
            setPriceAi({ loading: false, data });
            // Feb-2026 (User-Wunsch) — beim automatischen KI-Lauf (Öffnen der
            // Sektion / „Alle KI aktualisieren") leere Preisfelder direkt mit dem
            // Vorschlag befüllen. Bereits ausgefüllte Felder bleiben unangetastet
            // (Override-Schutz); die Vermittlungsprovision rechnet sich danach
            // automatisch aus Preis B2C − Preis B2B (siehe Margen-Effect oben).
            const applied = [];
            if (autoApply) {
                const b2cEmpty = form.price === "" || form.price == null;
                const b2bEmpty = form.price_b2b === "" || form.price_b2b == null;
                if (!isAuction && b2cEmpty && data.suggested_b2c > 0) {
                    update("price", String(data.suggested_b2c));
                    applied.push("B2C");
                }
                if (isDealer && !isAuction && b2bEmpty && data.suggested_b2b > 0) {
                    update("price_b2b", String(data.suggested_b2b));
                    applied.push("B2B");
                }
            }
            if (!silent) {
                if (data.mocked) {
                    toast.warning("KI nicht erreichbar — Fallback-Schätzung angezeigt.", { duration: 4000 });
                } else if (applied.length) {
                    toast.success(`KI-Preis übernommen (${applied.join(" + ")}) — Provision wird automatisch berechnet. Jederzeit überschreibbar.`, { duration: 5000 });
                } else {
                    toast.success(`KI-Preisvorschlag erstellt (${data.comparables_count} Vergleichs-Inserate).`, { duration: 4000 });
                }
            }
        } catch (err) {
            setPriceAi({ loading: false, data: null });
            if (!silent) {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "KI-Preisvorschlag fehlgeschlagen.");
            }
        }
    };

    return (
        <SectionToggle
            title="Preise & Qualität & Kalkulation"
            icon={CircleDollarSign}
            open={pricesOpen}
            onOpenChange={(v) => {
                if (v) {
                    if (closeBasicsIfOpen) closeBasicsIfOpen();
                    // Feb-2026 Iter 28 — KI in dieser Sektion automatisch
                    // aktualisieren, wenn der User sie öffnet (silent = kein
                    // unnötiger Toast-Spam).
                    runAllAi(true);
                }
                setPricesOpen(v);
            }}
            headerExtras={(
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); runAllAi(false); }}
                    disabled={priceAi.loading}
                    className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] disabled:opacity-50"
                    data-testid="prices-ai-refresh-btn"
                    title="Aktualisiert KI-Preisvorschlag, Quality-Score und Plausibilitäts-Check in dieser Sektion."
                >
                    {priceAi.loading
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> KI läuft…</>
                        : <><Sparkles className="w-3 h-3" /> Alle KI aktualisieren</>}
                </button>
            )}
            testId="prices-section"
        >
            <div className="bg-white border border-gray-200 rounded-sm p-6">
                {/* Feb-2026 (User-Wunsch Iter 28) — Titel-Feld sitzt jetzt ÜBER dem
                    Quality-Score-Card. Sobald der Nutzer den Titel ausfüllt, sieht
                    er sofort den Quality-Tipp „Aussagekräftiger Titel +10"
                    verschwinden und kann direkt darunter MwSt + Preise setzen.
                    Quelle: BasicsSection (Titel war dort der erste Eintrag). */}
                <div className="mb-3" data-testid="title-row">
                    <div className="flex items-center justify-between">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                            {t("create_listing.title_label")}
                        </Label>
                        {!orderMode && (
                        <button
                            type="button"
                            onClick={() => {
                                const ezY = form.first_registration_date
                                    ? new Date(form.first_registration_date).getFullYear()
                                    : form.year;
                                const km = Number(form.mileage) || 0;
                                const parts = [
                                    [form.brand, form.model].filter(Boolean).join(" "),
                                    ezY ? `EZ ${ezY}` : null,
                                    km > 0 ? `${km.toLocaleString("de-DE")} km` : null,
                                    form.condition === "Neufahrzeug" ? "Neu" : null,
                                ].filter(Boolean);
                                if (parts.length === 0) {
                                    toast.info("Bitte zuerst Marke + Modell ausfüllen.");
                                    return;
                                }
                                const suggestion = parts.join(", ");
                                update("title", suggestion);
                                toast.success("Titel-Vorschlag eingefügt — kannst du jederzeit anpassen.", { duration: 4000 });
                            }}
                            disabled={!form.brand || !form.model}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-gray-100 hover:bg-gray-200 text-[#111827] border border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                            data-testid="form-title-suggest-btn"
                            title="Generiert einen Titel aus Marke, Modell, Erstzulassung und Kilometerstand"
                        >
                            <Sparkles className="w-3 h-3" /> Vorschlag
                        </button>
                        )}
                    </div>
                    <Input
                        value={form.title}
                        onChange={(e) => update("title", e.target.value)}
                        required
                        className={`mt-1.5 rounded-sm ${reqCls("title")}`}
                        placeholder={t("create_listing.title_placeholder")}
                        data-testid="form-title"
                    />
                </div>

                {/* Feb-2026 (User-Wunsch Iter 28) — Quality-Score-Card sitzt jetzt
                    direkt ÜBER dem MwSt-Block in dieser Pricing-Sektion, damit der
                    Tipp „Aussagekräftiger Titel +10" o.ä. unmittelbar neben den
                    Preis-Entscheidungen sichtbar ist. */}
                {!orderMode && (
                <div className="mb-3" data-testid="quality-score-row">
                    <QualityScoreCard form={form} compact />
                </div>
                )}

                {/* Feb-2026 (User-Wunsch 27.05.2026) — GW-Quick-Preisermittlung
                    wurde vom Inserat-Editor ins Dashboard verschoben
                    (Quick-Action-Bar neben „Werkstatt-Aufträge"). */}

                {/* Feb-2026 Iter 28 — KI-Plausibilitäts-Check: warnt live vor
                    logischen Widersprüchen (Neufahrzeug + hohe KM, Preis-Mismatch,
                    kW↔PS, EZ in der Zukunft, etc.). `refreshSignal` zwingt eine
                    sofortige Neu-Auswertung (z.B. beim Öffnen der Sektion oder
                    Klick auf „Alle KI aktualisieren"). */}
                <PlausibilityCheckBanner form={form} refreshSignal={aiRefreshSeq} />

                {/* Feb-2026 (User-Wunsch Iter 28) — MwSt / Differenzbesteuerung sitzt jetzt
                    DIREKT über dem B2C-Preis, damit das Steuerregime unmittelbar vor der
                    Preiseingabe geklärt wird (fachliche Nähe). Quelle: BasicsSection. */}
                <div className={`mb-4 border rounded-sm p-3 ${orderMode ? "bg-amber-50 border-amber-400 ring-1 ring-amber-300" : "bg-gray-50 border-gray-200"}`} data-testid="vat-block">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.vat_included !== false}
                            onChange={(e) => update("vat_included", e.target.checked)}
                            className="mt-1 w-4 h-4 accent-[#111827]"
                            data-testid="vat-included"
                        />
                        <div>
                            <span className="font-bold text-sm text-[#111827]">Fahrzeug ist mit Mehrwertsteuer (MwSt. ausweisbar)</span>
                            {orderMode && <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-amber-700">✓ vorbelegt (Neufahrzeug)</span>}
                            <span className="ml-2 text-[11px] text-gray-500">— Hinweis: Bei <strong>nicht</strong> angehakt handelt es sich automatisch um ein <strong>differenzbesteuertes Fahrzeug</strong> (§ 25a UStG).</span>
                        </div>
                    </label>
                    {form.vat_included !== false && (
                        <div className="mt-3 ml-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="vat-export-options">
                            <label className="flex items-start gap-2 cursor-pointer text-sm">
                                <input
                                    type="radio"
                                    name="vat_export_choice"
                                    checked={!!form.vat_export_net_calculation}
                                    onChange={() => {
                                        update("vat_export_net_calculation", true);
                                        update("vat_export_refund_after_docs", false);
                                    }}
                                    className="mt-0.5 w-4 h-4 accent-[#111827]"
                                    data-testid="vat-export-net"
                                />
                                <span>
                                    <span className="font-medium">Bei Export wird das Fahrzeug netto berechnet</span>
                                    <span className="block text-[11px] text-gray-500">Direktverkauf ohne MwSt. an EU/Dritt­länder-Käufer.</span>
                                </span>
                            </label>
                            <label className="flex items-start gap-2 cursor-pointer text-sm">
                                <input
                                    type="radio"
                                    name="vat_export_choice"
                                    checked={!!form.vat_export_refund_after_docs}
                                    onChange={() => {
                                        update("vat_export_refund_after_docs", true);
                                        update("vat_export_net_calculation", false);
                                    }}
                                    className="mt-0.5 w-4 h-4 accent-[#111827]"
                                    data-testid="vat-export-refund"
                                />
                                <span>
                                    <span className="font-medium">Bei Export wird MwSt. erst mitbezahlt</span>
                                    <span className="block text-[11px] text-gray-500">Erstattung nach Einreichung aller notwendigen Unterlagen (z. B. Ausfuhrnachweis).</span>
                                </span>
                            </label>
                            {(form.vat_export_net_calculation || form.vat_export_refund_after_docs) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        update("vat_export_net_calculation", false);
                                        update("vat_export_refund_after_docs", false);
                                    }}
                                    className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-900 self-start sm:col-span-2"
                                    data-testid="vat-export-reset"
                                >
                                    Auswahl zurücksetzen
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 items-start" data-testid="prices-row">
                    {/* Jun-2026 (User-Wunsch) — Listenpreis (UVP / VKP brutto). Reine
                        Anzeige-/Kalkulationsgröße, wird beim Excel-Import befüllt. */}
                    <div data-testid="list-price-wrap">
                        <div className="flex items-center gap-1 min-h-[28px]">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Listenpreis (UVP)</Label>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-[#111827] focus:outline-none focus:text-[#111827]"
                                data-testid="form-list-price-info"
                                title="Brutto-Listenpreis (UVP) des Fahrzeugs. Beim Import aus VKP brutto. Optional."
                                aria-label="Info zum Listenpreis"
                            >
                                <Info className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="relative mt-1.5">
                            <Input
                                type="number"
                                value={form.list_price_gross ?? ""}
                                onChange={(e) => update("list_price_gross", e.target.value)}
                                disabled={isAuction}
                                className="rounded-sm disabled:bg-gray-100 font-mono-num pr-8"
                                data-testid="form-list-price-gross"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">€</span>
                        </div>
                        {netHint(form.list_price_gross, "form-list-price-net")}
                    </div>
                    <div>
                        <div className="flex items-center justify-between min-h-[28px]">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                Preis B2C (Privat)
                            </Label>
                            {!isAuction && (
                                <button
                                    type="button"
                                    onClick={fetchPriceSuggestion}
                                    disabled={priceAi.loading || !form.brand || !form.model}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-gray-100 hover:bg-gray-200 text-[#111827] border border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    data-testid="form-price-ai-btn"
                                    title="KI schlägt einen B2C- und B2B-Preis basierend auf Marke, Modell, Jahr, Kilometer & DB-Vergleichsinseraten vor."
                                >
                                    {priceAi.loading
                                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Lädt…</>
                                        : <><Sparkles className="w-3 h-3" /> KI-Preis</>}
                                </button>
                            )}
                        </div>
                        <div className="relative mt-1.5">
                            <Input
                                type="number"
                                value={form.price}
                                onChange={(e) => update("price", e.target.value)}
                                required={!isAuction}
                                disabled={isAuction}
                                className={`rounded-sm disabled:bg-gray-100 font-mono-num pr-8 ${reqCls("price")}`}
                                data-testid="form-price"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">€</span>
                        </div>
                        {netHint(form.price, "form-price-net")}
                        {/* Feb-2026 (User-Wunsch) — Marktwert-Konfidenz direkt unter
                            dem B2C-Preis: schafft Vertrauen & reduziert Preis-Rückfragen. */}
                        {!isAuction && priceAi.data && (priceAi.data.comparables_count > 0 || priceAi.data.range_min) && (
                            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-gray-600" data-testid="form-price-confidence">
                                <BarChart3 className="w-3.5 h-3.5 text-emerald-600 mt-px shrink-0" />
                                <span>
                                    <span className="font-bold text-gray-700">Marktwert-Konfidenz:</span>{" "}
                                    {priceAi.data.comparables_count > 0
                                        ? `basiert auf ${priceAi.data.comparables_count} Vergleichs-Inserat${priceAi.data.comparables_count !== 1 ? "en" : ""}`
                                        : "KI-Schätzung"}
                                    {priceAi.data.range_min && priceAi.data.range_max
                                        ? ` · Spanne ${Number(priceAi.data.range_min).toLocaleString("de-DE")}–${Number(priceAi.data.range_max).toLocaleString("de-DE")} €`
                                        : ""}
                                    {priceAi.data.mocked ? " (Heuristik)" : ""}
                                </span>
                            </div>
                        )}
                    </div>
                    {isDealer && (
                        <div data-testid="b2b-price-wrap">
                            <div className="flex items-center gap-1 min-h-[28px]">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Preis B2B (Gewerblich)</Label>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-[#111827] focus:outline-none focus:text-[#111827]"
                                    data-testid="form-price-b2b-info"
                                    title="Pflichtfeld."
                                    aria-label="Info zum B2B-Preis"
                                >
                                    <Info className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div className="relative mt-1.5">
                                <Input
                                    type="number"
                                    value={form.price_b2b}
                                    onChange={(e) => update("price_b2b", e.target.value)}
                                    disabled={isAuction}
                                    required
                                    className={`rounded-sm disabled:bg-gray-100 font-mono-num pr-8 ${reqCls("price_b2b")}`}
                                    data-testid="form-price-b2b"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">€</span>
                            </div>
                            {netHint(form.price_b2b, "form-price-b2b-net")}
                        </div>
                    )}
                    {isDealer && (
                        <div data-testid="referral-commission-wrap">
                            <div className="flex items-center gap-1 min-h-[28px]">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Vermittlungsprovision</Label>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-[#111827] focus:outline-none focus:text-[#111827]"
                                    data-testid="form-referral-commission-info"
                                    title="Pflichtfeld. Eingabe zwischen 50 und 600 €. Wird bei erfolgreicher Vermittlung über einen Tippgeber/Vermittler an diesen ausgezahlt."
                                    aria-label="Info zur Vermittlungsprovision"
                                >
                                    <Info className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div className="relative mt-1.5">
                                <Input
                                    type="number"
                                    min="0"
                                    step="1"
                                    required
                                    value={form.referral_commission_eur}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === "") return update("referral_commission_eur", "");
                                        const n = Math.max(0, parseInt(raw.replace(/\D/g, ""), 10) || 0);
                                        update("referral_commission_eur", String(n));
                                    }}
                                    placeholder="Auto: Preis B2C − Preis B2B"
                                    className={`rounded-sm font-mono-num pr-8 ${reqCls("referral_commission_eur")}`}
                                    data-testid="form-referral-commission"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">€</span>
                            </div>
                            {/* Feb-2026 (User-Wunsch 27.05.2026) — Margen-Rechner-Hinweis. */}
                            {Number(form.price) > 0 && Number(form.price_b2b) > 0 && (
                                <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-1" data-testid="commission-auto-hint">
                                    <Sparkles className="w-3 h-3 text-amber-500" />
                                    Auto-berechnet: <span className="font-mono-num font-bold">{Number(form.price).toLocaleString("de-DE")} € − {Number(form.price_b2b).toLocaleString("de-DE")} € = {Math.max(0, Math.round(Number(form.price) - Number(form.price_b2b))).toLocaleString("de-DE")} €</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Prominenter KI-Ladezustand — signalisiert den mehrsekündigen
                    Analyse-Vorgang deutlich (Shimmer-Skeleton der Ergebnis-Karte). */}
                {priceAi.loading && !priceAi.data && (
                    <div
                        className="mt-4 bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 rounded-sm p-4 overflow-hidden"
                        data-testid="price-ai-loading"
                        role="status"
                        aria-live="polite"
                    >
                        <div className="flex items-center gap-3">
                            <div className="relative w-11 h-11 shrink-0">
                                <span className="absolute inset-0 rounded-full bg-orange-300/40 animate-ping" />
                                <span className="absolute inset-0 flex items-center justify-center">
                                    <Hourglass className="w-6 h-6 text-[#F97316] animate-spin" style={{ animationDuration: "2.2s" }} />
                                </span>
                            </div>
                            <div className="min-w-0">
                                <div className="font-bold text-sm text-[#111827]">KI berechnet Preisvorschlag …</div>
                                <div className="text-[11px] text-orange-800">Vergleichsinserate &amp; Zustand werden analysiert — das dauert einige Sekunden.</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="bg-white/70 border border-orange-200 rounded-sm p-2.5">
                                    <div className="h-2.5 w-12 bg-orange-200/70 rounded animate-pulse" />
                                    <div className="h-4 w-16 bg-orange-300/60 rounded mt-2 animate-pulse" style={{ animationDelay: `${i * 120}ms` }} />
                                </div>
                            ))}
                        </div>
                        <div className="h-2.5 w-3/4 bg-orange-200/60 rounded mt-3 animate-pulse" />
                        <div className="h-2.5 w-2/3 bg-orange-200/50 rounded mt-2 animate-pulse" />
                    </div>
                )}

                {/* KI-Preisvorschlag-Result-Card. */}
                {priceAi.data && (
                    <div
                        className="mt-4 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-sm p-4"
                        data-testid="form-price-ai-result"
                    >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-amber-700" />
                                <span className="font-bold text-sm text-[#111827]">KI-Preisvorschlag</span>
                                <span className="text-[10px] font-mono uppercase bg-white border border-amber-300 px-1.5 py-0.5 rounded-sm text-amber-800">
                                    {priceAi.data.mocked ? "Heuristik" : priceAi.data.model || "KI"}
                                </span>
                                {priceAi.data.comparables_count > 0 && (
                                    <span className="text-[10px] text-gray-600">
                                        ({priceAi.data.comparables_count} Vergleich{priceAi.data.comparables_count !== 1 ? "e" : ""} aus DB)
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setPriceAi({ loading: false, data: null })}
                                className="text-[11px] text-gray-500 hover:text-gray-900"
                                data-testid="form-price-ai-dismiss"
                            >
                                ✕ schließen
                            </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                            <div>
                                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">B2C-Preis</div>
                                <div className="font-mono-num text-lg font-bold text-[#111827]" data-testid="form-price-ai-b2c">
                                    {priceAi.data.suggested_b2c ? `${Number(priceAi.data.suggested_b2c).toLocaleString("de-DE")} €` : "—"}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">B2B-Preis</div>
                                <div className="font-mono-num text-lg font-bold text-[#111827]" data-testid="form-price-ai-b2b">
                                    {priceAi.data.suggested_b2b ? `${Number(priceAi.data.suggested_b2b).toLocaleString("de-DE")} €` : "—"}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Min. Range</div>
                                <div className="font-mono-num text-sm text-gray-700">
                                    {priceAi.data.range_min ? `${Number(priceAi.data.range_min).toLocaleString("de-DE")} €` : "—"}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Max. Range</div>
                                <div className="font-mono-num text-sm text-gray-700">
                                    {priceAi.data.range_max ? `${Number(priceAi.data.range_max).toLocaleString("de-DE")} €` : "—"}
                                </div>
                            </div>
                        </div>
                        {priceAi.data.rationale && (
                            <p className="mt-2 text-[12px] text-gray-700 italic" data-testid="form-price-ai-rationale">
                                „{priceAi.data.rationale}"
                            </p>
                        )}
                        <p className="mt-2 text-[10px] text-gray-500" data-testid="form-price-ai-disclosure">
                            KI-generierte, unverbindliche Schätzung auf Basis von Vergleichsdaten – keine Gewähr. Die finale Preisentscheidung treffen Sie.
                        </p>
                        {/* Jul-2026 (User-Wunsch) — Kalkulations-Kontext IMMER sichtbar
                            (auch bei 0), damit Einkaufspreis, Werkstattaufträge &
                            Schadensermittlung für die Preisentscheidung auf einen Blick da sind. */}
                        <div className="mt-3 border-t border-orange-200 pt-3 space-y-1" data-testid="price-ai-calc-context">
                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1">Kalkulations-Kontext (auf einen Blick)</div>
                            <div className="flex items-center justify-between text-[12px]">
                                <span className="text-gray-600">Einkaufspreis</span>
                                <span className="font-mono-num font-bold text-[#111827]" data-testid="price-ai-ek">{ekVal > 0 ? `${ekVal.toLocaleString("de-DE")} €` : "—"}</span>
                            </div>
                            <div className="flex items-center justify-between text-[12px]">
                                <span className="text-gray-600">Werkstattaufträge (interne Kosten)</span>
                                <span className="font-mono-num font-bold text-[#111827]" data-testid="price-ai-workshop">
                                    {workshopTotal > 0
                                        ? `${workshopTotal.toLocaleString("de-DE")} €`
                                        : (costSummary?.count ? `${costSummary.count} Auftrag/-e` : "keine")}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-[12px]">
                                <span className="text-gray-600">Schadensermittlung (Abzug)</span>
                                <span className="font-mono-num font-bold text-orange-700" data-testid="price-ai-damage">{damageDed > 0 ? `− ${damageDed.toLocaleString("de-DE")} €` : "—"}</span>
                            </div>
                            {damageDed > 0 && (
                                <label className="flex items-center gap-2 text-[12px] text-gray-700 cursor-pointer pt-1">
                                    <input type="checkbox" checked={includeDamage} onChange={(e) => setIncludeDamage(e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="price-ai-include-damage" />
                                    Schadensabzug bei der Preisermittlung berücksichtigen
                                </label>
                            )}
                            {includeDamage && damageDed > 0 && (
                                <div className="grid grid-cols-2 gap-3 pt-1" data-testid="price-ai-after-deduction">
                                    <div>
                                        <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">B2C nach Abzug</div>
                                        <div className="font-mono-num text-base font-bold text-emerald-700" data-testid="price-ai-b2c-after">
                                            {priceAi.data.suggested_b2c ? `${Math.max(0, Number(priceAi.data.suggested_b2c) - damageDed).toLocaleString("de-DE")} €` : "—"}
                                        </div>
                                    </div>
                                    {isDealer && (
                                        <div>
                                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">B2B nach Abzug</div>
                                            <div className="font-mono-num text-base font-bold text-emerald-700" data-testid="price-ai-b2b-after">
                                                {priceAi.data.suggested_b2b ? `${Math.max(0, Number(priceAi.data.suggested_b2b) - damageDed).toLocaleString("de-DE")} €` : "—"}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {priceAi.data.suggested_b2c > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        update("price", String(priceAi.data.suggested_b2c));
                                        toast.success("B2C-Preis übernommen.");
                                    }}
                                    className="h-8 px-3 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-[10px]"
                                    data-testid="form-price-ai-apply-b2c"
                                >
                                    → B2C übernehmen
                                </button>
                            )}
                            {isDealer && priceAi.data.suggested_b2b > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        update("price_b2b", String(priceAi.data.suggested_b2b));
                                        toast.success("B2B-Preis übernommen.");
                                    }}
                                    className="h-8 px-3 bg-white hover:bg-gray-100 border border-[#111827] text-[#111827] font-bold rounded-sm uppercase tracking-wider text-[10px]"
                                    data-testid="form-price-ai-apply-b2b"
                                >
                                    → B2B übernehmen
                                </button>
                            )}
                            {priceAi.data.suggested_b2c > 0 && isDealer && priceAi.data.suggested_b2b > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        update("price", String(priceAi.data.suggested_b2c));
                                        update("price_b2b", String(priceAi.data.suggested_b2b));
                                        toast.success("B2C + B2B übernommen.");
                                    }}
                                    className="h-8 px-3 bg-[#FFCC00] hover:bg-yellow-300 text-[#111827] font-bold rounded-sm uppercase tracking-wider text-[10px]"
                                    data-testid="form-price-ai-apply-both"
                                >
                                    Beide übernehmen
                                </button>
                            )}
                            {includeDamage && damageDed > 0 && priceAi.data.suggested_b2c > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const b2c = Math.max(0, Number(priceAi.data.suggested_b2c) - damageDed);
                                        update("price", String(b2c));
                                        if (isDealer && priceAi.data.suggested_b2b > 0) {
                                            const b2b = Math.max(0, Number(priceAi.data.suggested_b2b) - damageDed);
                                            update("price_b2b", String(b2b));
                                        }
                                        toast.success("Preis(e) nach Schadensabzug übernommen.");
                                    }}
                                    className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm uppercase tracking-wider text-[10px]"
                                    data-testid="form-price-ai-apply-after-deduction"
                                >
                                    → Nach Abzug übernehmen
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Feb-2026 — Schadensermittlung-Block unterhalb des Listenpreises
                    auf User-Wunsch (Jul-2026) entfernt. Der Schadensabzug bleibt in der
                    Kalkulation über den Zustand/andere Quellen erhalten. */}
                {/* Jul-2026 (User-Wunsch) — „Einstand & Kalkulation" wurde in den Bereich
                    „Zustand & Schadensermittlung" integriert (siehe CreateListingPage
                    beforePriceSlot) und ist hier entfernt. */}
            </div>
        </SectionToggle>
    );
}
